import React, { useCallback } from 'react';

import { CrossIcon } from '@vlabs/icons';
import { GroupedCheckboxItem } from '@vlabs/monokit/shared/ui/grouped-checkbox';
import { Control } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import st from './shared.module.sass';
import { removeValue, toggleValue } from '../utils/array';

interface AddedSourcesGroupProps {
  visibleCustomSourceNames: string[];
  customSourceNames: string[];
  selectedSources: string[];
  onChangeCustomSourceNames: (next: string[]) => void;
  onChangeSelectedSource: (next: string[]) => void;
}

export function AddedSourcesGroup({
  visibleCustomSourceNames,
  customSourceNames,
  selectedSources,
  onChangeCustomSourceNames,
  onChangeSelectedSource,
}: AddedSourcesGroupProps): React.JSX.Element | null {
  const { t } = useTranslation();

  const onToggle = useCallback(
    (value: string, nextChecked: boolean) => {
      onChangeSelectedSource(toggleValue(selectedSources, value, nextChecked));
    },
    [onChangeSelectedSource, selectedSources],
  );

  const onRemove = useCallback(
    (value: string) => {
      onChangeCustomSourceNames(removeValue(customSourceNames, value));
      onChangeSelectedSource(removeValue(selectedSources, value));
    },
    [customSourceNames, onChangeCustomSourceNames, onChangeSelectedSource, selectedSources],
  );

  if (visibleCustomSourceNames.length === 0) return null;

  return (
    <div className={st.Group}>
      <div className={st.GroupHeader}>{t('sources.added source title')}</div>

      {visibleCustomSourceNames.map((name) => (
        <GroupedCheckboxItem
          checked={selectedSources.includes(name)}
          key={name}
          label={name}
          onToggle={onToggle}
          rightSlot={(
            <Control.IconButton
              icon={<CrossIcon />}
              kind="secondary"
              onClick={() => onRemove(name)}
              size="small"
              variant="flat"
            />
          )}
          value={name}
        />
      ))}
    </div>
  );
}
